import {
  CheckboxField,
  DateField,
  FileField,
  HiddenField,
  MarkdownField,
  TextField,
} from "@components/fields";
import { FlashMessage } from "@components/flash_messages/flash_message";
import { useMutate } from "@hooks/use_mutate";
import { ApiModelError } from "@root/types";
import { api } from "@services/api";
import { appRoutes } from "@services/app_routes";
import { isNil, prepareFormikInitialValues } from "@services/typed_helpers";
import { Form, Formik } from "formik";
import React, { FC, useMemo } from "react";

import * as types from "../../../types";

interface StudioEventFormProps {
  studioEvent: types.StudioEventModel;
}

const transformToFormInputs = (
  serverData: types.StudioEventModel,
): types.StudioEventFormikValues => {
  // pulling displayImage, bannerImage and slug out so we can just use the
  // rest of data without long-hand building a new object
  const {
    id,
    displayImageId: _displayImageId,
    bannerImageId: _bannerImageId,
    publishedAt,
    slug: _slug,
    ...data
  } = serverData;

  const inputData = {
    ...data,
    id: id?.toString(),
    published: !!publishedAt,
    bannerImage: undefined,
    displayImage: undefined,
  };

  return inputData;
};

const StudioEventsForm: FC<StudioEventFormProps> = ({ studioEvent }) => {
  const updateStudioEvent = useMemo(() => {
    return (token: string) =>
      (studioEventData?: types.StudioEventPostValues) => {
        if (isNil(studioEventData)) {
          throw "Studio event data must be present!";
        }
        if (studioEventData.id) {
          return api.studioEvents.update(token, studioEventData);
        } else {
          return api.studioEvents.create(token, studioEventData!);
        }
      };
  }, []);

  const initialValues = useMemo(
    () =>
      prepareFormikInitialValues<
        types.StudioEventFormikValues,
        types.StudioEventFormikValues
      >(transformToFormInputs(studioEvent)),
    [studioEvent],
  );

  const {
    submitData: submit,
    loading,
    error: apiError,
    data,
  } = useMutate<types.StudioEventUpdateResponse, types.StudioEventPostValues>(
    updateStudioEvent,
  );
  const { errors } = data ?? {};
  const handleSubmit = (values: types.StudioEventFormikValues) => {
    if (loading) {
      return;
    }
    const postValues = {
      ...values,
      published: values.published.toString(),
    };
    submit(postValues).then((data) => {
      if (data?.success) {
        const currentUrl = new URL(window.location.href);
        currentUrl.pathname = appRoutes.admin.studioEvents.index();

        window.location.replace(currentUrl);
      }
    });
  };

  const formatErrors = (errors: ApiModelError): string[] => {
    return Object.entries(errors).flatMap(([fieldName, errorWords]) => {
      return errorWords.map(
        (error) =>
          `${fieldName[0].toUpperCase() + fieldName.slice(1)} ${error}`,
      );
    });
  };

  return (
    <>
      {errors ? (
        <FlashMessage message={formatErrors(errors)} type="error" />
      ) : null}
      {apiError ? (
        <FlashMessage
          message="Something terrible has happened.  Please try again later."
          type="error"
        />
      ) : null}
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        id="studio_event_form"
      >
        <Form encType="multipart/form-data" method="post">
          <HiddenField id="studioEventId" name="id" />
          <TextField
            name="headline"
            label="Headline"
            helpElement="e.g. 1890 Bryant Holiday Show & Sale"
            required
          />
          <MarkdownField name="description" label="Description" />
          <FileField
            name="bannerImage"
            label="Banner Image"
            helpElement="This image will be shown on the home page when the event is active (see dates below).  For best results, use an aspect ratio close to 2.3x1 (landscape 1400x600)"
            accept="image/*"
          />
          <FileField
            name="displayImage"
            label="Display Image"
            helpElement="This image will be used on the event detail page and should be more like a postcard. For best results, use an aspect ratio close to 4x5 (portrait 800x1000)."
            accept="image/*"
          />
          <DateField
            name="startDate"
            label="Event start date"
            helpElement="This date is the first day of the event.  It's used for sorting events into past, current and future but is not use for display.  Any date info should be included in the description."
          />
          <DateField
            name="activateDate"
            label="Activate on"
            helpElement={`If an event is "active", we show it on the home page.
           The event is active if it is published (checkbox below) and the
          current date is between this activation date and the deactivation date below.`}
          />
          <DateField
            name="deactivateDate"
            label="Deactivate on"
            helpElement="Used in conjuction with activte on above."
          />
          <CheckboxField
            name="published"
            label="Published"
            helpElement="When checked, this event will be visible on the website."
          />
          <div className="admin-form-controls">
            <div className="admin-form-controls--button">
              <a
                role="button"
                href="/admin/studio_events"
                className="secondary"
              >
                Cancel
              </a>
            </div>
            <div className="admin-form-controls--button">
              <input
                type="submit"
                value={studioEvent.id ? "Update" : "Create"}
              />
            </div>
          </div>
        </Form>
      </Formik>
    </>
  );
};

export { StudioEventsForm };
